/**
 * Fetch application list from Gobodo (Application__c records).
 * Used for page layout and record page application dropdowns.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { retryWithBackoff } from '../../utils/gobodoRetry';

const APPLICATION_OBJECT_KEY = 'Application__c';

export interface ApplicationOption {
  value: string;
  label: string;
}

export async function fetchGobodoApplications(): Promise<ApplicationOption[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const records = await retryWithBackoff(
    () =>
      objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), APPLICATION_OBJECT_KEY, {
        page: 0,
        size: 100,
        cachekey: ['gobodo-applications'],
        cacheduration: '60',
      }),
    { maxRetries: 3 }
  );
  const list: ApplicationOption[] = (records || []).map((r: any) => {
    const p = r.properties || r.data || {};
    return {
      value: p.Value__c ?? p.value ?? '',
      label: p.Label__c ?? p.label ?? p.Value__c ?? p.value ?? '',
    };
  }).filter((a: ApplicationOption) => a.value);
  list.sort((a, b) => {
    const orderA = (records as any[]).find((r: any) => (r.properties || r.data || {}).Value__c === a.value);
    const orderB = (records as any[]).find((r: any) => (r.properties || r.data || {}).Value__c === b.value);
    const oA = orderA ? (orderA.properties || orderA.data || {}).Order__c ?? 999 : 999;
    const oB = orderB ? (orderB.properties || orderB.data || {}).Order__c ?? 999 : 999;
    return Number(oA) - Number(oB);
  });
  return list;
}
